import { compareDisplayNames } from '@utils/string.util';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import {
  AssetTypeListTableComponent,
  AssetTypeModel,
} from './asset-type-list-table/asset-type-list-table.component';
import { AssetTypeService } from '@services/api/asset-type.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  AssetTypeEditDialogComponent,
  AssetTypeResponseModel,
  DeviceTypeViewModel,
  DialogOption,
} from './asset-type-edit-dialog/asset-type-edit-dialog.component';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { AssetTypeRequest } from '@entities/assetType';
import { MasterdataService } from '@services/api/masterdata.service';
import { DeviceTypeResponse } from '@entities/deviceType';
import { AccountResponse } from '@entities/account';

/** コピー時に追加する文字列 */
const COPY_SUFFIX = '_コピー';

/** 取得するマスターデータ*/
const dataType = ['device_type'];

/** 衝撃検知デフォルト値 */
const SHOCK_DETECTED_DEFAULT_VALUE = 5;

/** 権限 */
export type RoleViewModel =
  | 'system_admin'
  | 'tenant_admin'
  | 'tenant_editor'
  | 'tenant_viewer';

@Component({
  selector: 'app-asset-type-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    MatCardModule,
    AssetTypeListTableComponent,
  ],
  templateUrl: './asset-type-list.component.html',
  styleUrl: './asset-type-list.component.scss',
})
export class AssetTypeListComponent implements OnInit {
  /** ボタンの無効化状態 */
  public isDisabled: boolean = false;

  /** 設定テンプレート一覧 */
  public assetTypeList: AssetTypeModel[] = [];

  /** デバイス種類一覧 */
  public deviceTypeList: DeviceTypeViewModel[] = [];

  /** 設定テンプレートのリスト */
  public deviceTypes: string[] = [];

  /** 画面を操作しているユーザの名前 */
  private _myName: string = '';

  constructor(
    private assetTypeService: AssetTypeService,
    private masterdataService: MasterdataService,
    private dialog: MatDialog,
    @Inject(ActivatedRoute) private route: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    // ログインユーザー情報を取得
    const loggedInUser = this.route.snapshot.data[
      'loggedInUser'
    ] as AccountResponse;
    // 追加及び更新時に必要になるためログインユーザーの名前を取得
    this._myName = loggedInUser.userName;
    // ログインユーザーの権限を取得する
    const loggedInUserRole: RoleViewModel = loggedInUser.tenant.roleId;

    // ユーザ種別を設定する
    switch (loggedInUserRole) {
      case 'system_admin':
        this.isDisabled = true;
        break;
      case 'tenant_admin':
        this.isDisabled = false;
        break;
      case 'tenant_editor':
        this.isDisabled = false;
        break;
      default:
        this.isDisabled = true;
    }

    // 設定テンプレート一覧を取得
    this._initAssetTypeList();

    // マスターデータを取得する
    this.masterdataService.find(dataType).subscribe({
      next: (masterdata) => {
        this.deviceTypeList = masterdata['deviceType'] as DeviceTypeResponse[];
      },
    });
  }

  /**
   * 設定テンプレート追加/編集ダイアログを表示する
   * @param assetType 設定テンプレート
   */
  public async openAssetTypeEditDialog(assetType?: AssetTypeModel) {
    const tenantId = this.route.snapshot.data['loggedInUser'].tenant.tenantId;
    const user = this.route.snapshot.data['loggedInUser'] as AccountResponse;
    const userId = user.userId;

    this.masterdataService.find(dataType).subscribe({
      next: async (masterdata) => {
        this.deviceTypeList = masterdata['deviceType'] as DeviceTypeResponse[];

        const options: DialogOption = {
          updating: false,
          deviceTypes: this.deviceTypeList,
          tenantId: tenantId,
          userId: userId,
          isDisabled: this.isDisabled,
        };

        const assetTypeList = {
          assettypeId: assetType?.assettypeId || '',
          assettypeName: assetType?.assettypeName || '',
          tenantId: tenantId,
          deviceActiveStatus: assetType?.deviceActiveStatus || false,
          deviceTypeId: assetType?.deviceTypeId || '',
          deviceTypeKey: assetType?.deviceTypeKey || '',
          samplingFrequency: assetType?.samplingFrequency || null,
          reportingFrequency: assetType?.reportingFrequency || null,
          shipmentTracking: assetType?.shipmentTracking || false,
          locationDetected: assetType?.locationDetected || false,
          locationPriority: [
            assetType?.locationPriority_01 || '',
            assetType?.locationPriority_02 || '',
            assetType?.locationPriority_03 || '',
          ],
          fallDetected: assetType?.fallDetected || false,
          mcdDetected: assetType?.mcdDetected || false,
          lightExposureDetected: assetType?.lightExposureDetected || false,
          lightExposureDetectValue: assetType?.lightExposureDetectValue || '',
          shockDetected: assetType?.shockDetected || false,
          shockDetectedDetectValue: assetType?.shockDetectedDetectValue || 0,
          temperature: assetType?.temperature || false,
          temperatureSettings: [
            assetType?.temperatureAlertMax || 0,
            assetType?.temperaturePrealertMax || 0,
            assetType?.temperaturePrealertMin || 0,
            assetType?.temperatureAlertMin || 0,
          ],
          pressure: assetType?.pressure || false,
          pressureSettings: [
            assetType?.pressureAlertMax || 0,
            assetType?.pressurePrealertMax || 0,
            assetType?.pressurePrealertMin || 0,
            assetType?.pressureAlertMin || 0,
          ],
          humidity: assetType?.humidity || false,
          humiditySettings: [
            assetType?.humidityAlertMax || 0,
            assetType?.humidityPrealertMax || 0,
            assetType?.humidityPrealertMin || 0,
            assetType?.humidityAlertMin || 0,
          ],
          tilt: assetType?.tilt || false,
          tiltSettings: [
            assetType?.tiltAlertMax || 0,
            assetType?.tiltPrealertMax || 0,
            assetType?.tiltPrealertMin || 0,
            assetType?.tiltAlertMin || 0,
          ],
          light: assetType?.light || false,
          lightSettings: [
            assetType?.lightAlertMax || 0,
            assetType?.lightPrealertMax || 0,
            assetType?.lightPrealertMin || 0,
            assetType?.lightAlertMin || 0,
          ],
          fmd: assetType?.fmd || false,
          createdBy: assetType?.createdBy || '',
          updatedBy: assetType?.updatedBy || '',
          createdAt: assetType?.createdAt || '',
          updatedAt: assetType?.updatedAt || '',
        };

        if (assetType) {
          options.updating = true;
          options.target = assetTypeList;
        }

        const editedAssetType: AssetTypeResponseModel = await firstValueFrom(
          this.dialog
            .open(AssetTypeEditDialogComponent, {
              width: '90%',
              maxWidth: '1300px',
              data: options,
            })
            .afterClosed(),
        );

        if (!editedAssetType) {
          return;
        }

        // 追加時の値を初期値として設定(更新時はupdatedByを編集しているユーザーネームに変える)
        const assetTypeRequest: AssetTypeRequest = {
          assettypeId: editedAssetType.assetTypeId || '',
          assettypeName: editedAssetType.assetTypeName,
          tenantId: options.tenantId,
          deviceActiveStatus: editedAssetType.deviceActiveStatus || false,
          deviceTypeId: editedAssetType.deviceTypeId,
          deviceTypeKey: editedAssetType.deviceTypeKey,
          reportingFrequency: editedAssetType.reportingFrequency ?? 0,
          shipmentTracking: editedAssetType.shipmentTracking,
          createdBy: this._myName,
          updatedBy: this._myName,
          createdAt: editedAssetType.createdAt,
          updatedAt: editedAssetType.updatedAt,
        };

        const samplingFrequency = editedAssetType.samplingFrequency;
        if (samplingFrequency != null) {
          assetTypeRequest.samplingFrequency = samplingFrequency;
        }

        // 有効化されているものだけ値を追加する
        const locationDetected = editedAssetType.locationDetected;
        if (locationDetected != null) {
          assetTypeRequest.locationDetected = locationDetected;
          assetTypeRequest.locationPriority_01 =
            editedAssetType.locationPriority_01 || '';
          assetTypeRequest.locationPriority_02 =
            editedAssetType.locationPriority_02 || '';
          assetTypeRequest.locationPriority_03 =
            editedAssetType.locationPriority_03 || '';
        }
        const fallDetected = editedAssetType.fallDetected;
        if (fallDetected != null) {
          assetTypeRequest.fallDetected = fallDetected;
        }
        const mcdDetected = editedAssetType.mcdDetected;
        if (mcdDetected != null) {
          assetTypeRequest.mcdDetected = mcdDetected;
        }
        const lightExposureDetected = editedAssetType.lightExposureDetected;
        if (lightExposureDetected != null) {
          assetTypeRequest.lightExposureDetected = lightExposureDetected;
          assetTypeRequest.lightExposureDetectValue =
            editedAssetType.lightExposureDetectValue;
        }
        const shockDetected = editedAssetType.shockDetected;
        if (shockDetected != null) {
          assetTypeRequest.shockDetected = shockDetected;
          assetTypeRequest.shockDetectedDetectValue =
            editedAssetType.shockDetectedDetectValue;
        }
        const temperature = editedAssetType.temperature;
        if (temperature != null) {
          assetTypeRequest.temperature = temperature;
          assetTypeRequest.temperatureAlertMax =
            editedAssetType.temperatureAlertMax || 0;
          assetTypeRequest.temperatureAlertMin =
            editedAssetType.temperatureAlertMin || 0;
          assetTypeRequest.temperaturePrealertMax =
            editedAssetType.temperaturePrealertMax || 0;
          assetTypeRequest.temperaturePrealertMin =
            editedAssetType.temperaturePrealertMin || 0;
        }
        const pressure = editedAssetType.pressure;
        if (pressure != null) {
          assetTypeRequest.pressure = pressure;
          assetTypeRequest.pressureAlertMax =
            editedAssetType.pressureAlertMax || 0;
          assetTypeRequest.pressureAlertMin =
            editedAssetType.pressureAlertMin || 0;
          assetTypeRequest.pressurePrealertMax =
            editedAssetType.pressurePrealertMax || 0;
          assetTypeRequest.pressurePrealertMin =
            editedAssetType.pressurePrealertMin || 0;
        }
        const humidity = editedAssetType.humidity;
        if (humidity != null) {
          assetTypeRequest.humidity = humidity;
          assetTypeRequest.humidityAlertMax =
            editedAssetType.humidityAlertMax || 0;
          assetTypeRequest.humidityAlertMin =
            editedAssetType.humidityAlertMin || 0;
          assetTypeRequest.humidityPrealertMax =
            editedAssetType.humidityPrealertMax || 0;
          assetTypeRequest.humidityPrealertMin =
            editedAssetType.humidityPrealertMin || 0;
        }
        const tilt = editedAssetType.tilt;
        if (tilt != null) {
          assetTypeRequest.tilt = tilt;
          assetTypeRequest.tiltAlertMax = editedAssetType.tiltAlertMax || 0;
        }
        const light = editedAssetType.light;
        if (light != null) {
          assetTypeRequest.light = light;
          assetTypeRequest.lightAlertMax = editedAssetType.lightAlertMax || 0;
          assetTypeRequest.lightAlertMin = editedAssetType.lightAlertMin || 0;
          assetTypeRequest.lightPrealertMax =
            editedAssetType.lightPrealertMax || 0;
          assetTypeRequest.lightPrealertMin =
            editedAssetType.lightPrealertMin || 0;
        }
        const fmd = editedAssetType.fmd;
        if (fmd != null) {
          assetTypeRequest.fmd = fmd;
        }

        // 更新時
        if (options.updating) {
          assetTypeRequest.createdBy = editedAssetType.createdBy;
          this.assetTypeService.update(assetTypeRequest).subscribe({
            next: () => {
              // 設定テンプレート一覧を再取得する
              this._initAssetTypeList();
            },
            error: (error) => {
              let alertDialogOptions: AlertDialogOption;
              if (
                error.response.status == 400 &&
                error.response.error.detail.type == 'NotFoundWebException'
              ) {
                alertDialogOptions = {
                  type: 'error',
                  message: `他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。`,
                };
                this.dialog.open(AlertDialogComponent, {
                  width: '600px',
                  data: alertDialogOptions,
                });
              } else if (
                error.response.error.detail.type == 'AlreadyExistsWebException'
              ) {
                alertDialogOptions = {
                  type: 'error',
                  message: `設定テンプレート名が重複しているため、更新に失敗しました。\n別の名前でもう一度お試しください。`,
                };
                this.dialog.open(AlertDialogComponent, {
                  width: '600px',
                  data: alertDialogOptions,
                });
              } else {
                this.handleApiError(error.response.error.detail.msg);
              }
            },
          });
        }
        // 追加時
        else {
          this.assetTypeService.add(assetTypeRequest).subscribe({
            next: () => {
              // 設定テンプレート一覧を再取得する
              this._initAssetTypeList();
            },
            error: (error) => {
              let message = '';
              if (
                error.response.error.detail.type == 'AlreadyExistsWebException'
              ) {
                message = `設定テンプレート名が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
                const alertDialogOptions: AlertDialogOption = {
                  type: 'error',
                  message: message,
                };
                this.dialog.open(AlertDialogComponent, {
                  width: '600px',
                  data: alertDialogOptions,
                });
              } else {
                this.handleApiError(error.response.error.detail.msg);
              }
            },
          });
        }
      },
      error: () => {},
    });
  }

  /**
   * 設定テンプレートをコピーし追加する
   * @param selectedAsset 選択された設定テンプレート
   */
  public async copiedAssetType(selectedAsset: AssetTypeModel) {
    const tenantId = this.route.snapshot.data['loggedInUser'].tenant.tenantId;

    // コピー確認画面の設定
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `設定テンプレート「${selectedAsset.assettypeName}」をコピーした、設定テンプレートを新規作成しますか？`,
      operation: 'コピー',
    };

    // コピー確認画面の表示と操作結果の取得
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    const assetTypeRequest: AssetTypeRequest = {
      assettypeId: '',
      assettypeName: selectedAsset.assettypeName,
      tenantId: tenantId,
      deviceActiveStatus: selectedAsset.deviceActiveStatus,
      deviceTypeId: selectedAsset.deviceTypeId,
      deviceTypeKey: selectedAsset.deviceTypeKey,
      samplingFrequency: selectedAsset.samplingFrequency,
      reportingFrequency: selectedAsset.reportingFrequency,
      shipmentTracking: selectedAsset.shipmentTracking,
      createdBy: this._myName,
      updatedBy: this._myName,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // 有効化されているものだけ値を追加する
    const locationDetected = selectedAsset.locationDetected;
    if (locationDetected != null) {
      assetTypeRequest.locationDetected = locationDetected;
      if (locationDetected) {
        assetTypeRequest.locationPriority_01 =
          selectedAsset.locationPriority_01 || '';
        assetTypeRequest.locationPriority_02 =
          selectedAsset.locationPriority_02 || '';
        assetTypeRequest.locationPriority_03 =
          selectedAsset.locationPriority_03 || '';
      }
    }
    const fallDetected = selectedAsset.fallDetected;
    if (fallDetected != null) {
      assetTypeRequest.fallDetected = fallDetected;
    }
    const mcdDetected = selectedAsset.mcdDetected;
    if (mcdDetected != null) {
      assetTypeRequest.mcdDetected = mcdDetected;
    }
    const lightExposureDetected = selectedAsset.lightExposureDetected;
    if (lightExposureDetected != null) {
      assetTypeRequest.lightExposureDetected = lightExposureDetected;
      if (lightExposureDetected) {
        assetTypeRequest.lightExposureDetectValue =
          selectedAsset.lightExposureDetectValue;
      } else {
        assetTypeRequest.lightExposureDetectValue = '';
      }
    }
    const shockDetected = selectedAsset.shockDetected;
    if (shockDetected != null) {
      assetTypeRequest.shockDetected = shockDetected;
      if (shockDetected) {
        assetTypeRequest.shockDetectedDetectValue =
          selectedAsset.shockDetectedDetectValue;
      } else {
        assetTypeRequest.shockDetectedDetectValue =
          SHOCK_DETECTED_DEFAULT_VALUE;
      }
    }
    const temperature = selectedAsset.temperature;
    if (temperature != null) {
      assetTypeRequest.temperature = temperature;
      if (temperature) {
        assetTypeRequest.temperatureAlertMax =
          selectedAsset.temperatureAlertMax || 0;
        assetTypeRequest.temperatureAlertMin =
          selectedAsset.temperatureAlertMin || 0;
        assetTypeRequest.temperaturePrealertMax =
          selectedAsset.temperaturePrealertMax || 0;
        assetTypeRequest.temperaturePrealertMin =
          selectedAsset.temperaturePrealertMin || 0;
      }
    }
    const pressure = selectedAsset.pressure;
    if (pressure != null) {
      assetTypeRequest.pressure = pressure;
      if (pressure) {
        assetTypeRequest.pressureAlertMax = selectedAsset.pressureAlertMax || 0;
        assetTypeRequest.pressureAlertMin = selectedAsset.pressureAlertMin || 0;
        assetTypeRequest.pressurePrealertMax =
          selectedAsset.pressurePrealertMax || 0;
        assetTypeRequest.pressurePrealertMin =
          selectedAsset.pressurePrealertMin || 0;
      }
    }
    const humidity = selectedAsset.humidity;
    if (humidity != null) {
      assetTypeRequest.humidity = humidity;
      if (humidity) {
        assetTypeRequest.humidityAlertMax = selectedAsset.humidityAlertMax || 0;
        assetTypeRequest.humidityAlertMin = selectedAsset.humidityAlertMin || 0;
        assetTypeRequest.humidityPrealertMax =
          selectedAsset.humidityPrealertMax || 0;
        assetTypeRequest.humidityPrealertMin =
          selectedAsset.humidityPrealertMin || 0;
      }
    }
    const tilt = selectedAsset.tilt;
    if (tilt != null) {
      assetTypeRequest.tilt = tilt;
      if (tilt) {
        assetTypeRequest.tiltAlertMax = selectedAsset.tiltAlertMax || 0;
      }
    }
    const light = selectedAsset.light;
    if (light != null) {
      assetTypeRequest.light = light;
      if (light) {
        assetTypeRequest.lightAlertMax = selectedAsset.lightAlertMax || 0;
        assetTypeRequest.lightAlertMin = selectedAsset.lightAlertMin || 0;
        assetTypeRequest.lightPrealertMax = selectedAsset.lightPrealertMax || 0;
        assetTypeRequest.lightPrealertMin = selectedAsset.lightPrealertMin || 0;
      }
    }
    const fmd = selectedAsset.fmd;
    if (fmd != null) {
      assetTypeRequest.fmd = fmd;
    }

    if (result) {
      // 設定テンプレートのリクエスト情報を更新
      const updatedAssetTypeRequest: AssetTypeRequest =
        this.appendAssetTypeName(assetTypeRequest);

      this.assetTypeService.add(updatedAssetTypeRequest).subscribe({
        next: () => {
          // 設定テンプレート一覧を再取得する
          this._initAssetTypeList();
        },
        error: (error) => {
          this.handleApiError(error.response.error.detail.msg);
        },
      });
    }
  }

  /**
   * 選択された設定テンプレートを削除する
   * @param selectedAssetList 選択された設定テンプレートリスト
   */
  public async deletedAssetType(selectedAssetList: AssetTypeModel[]) {
    // 削除確認画面の設定
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択した設定テンプレート ${selectedAssetList.length} 件を本当に削除してもよろしいですか？`,
      operation: '削除',
    };

    // 削除確認画面の表示と操作結果の取得
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    // 削除実行
    if (result) {
      const assetTypeIdList: string[] = selectedAssetList.map(
        (asset) => asset.assettypeId,
      );

      this.assetTypeService.delete(assetTypeIdList).subscribe({
        next: () => {
          // 設定テンプレート一覧を再取得する
          this._initAssetTypeList();
        },
        error: (error) => {
          this.handleApiError(error.response.error.detail.msg);
        },
      });
    }
  }

  /**
   * エラーレスポンスの判定を行いダイアログを表示する
   * @param errorResponse エラーレスポンス
   * @returns
   */
  private handleApiError(errorResponse: string) {
    const defaultMessage = `設定テンプレートの変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
    const alertDialogOptions: AlertDialogOption = {
      type: 'error',
      message: defaultMessage,
    };

    if (errorResponse) {
      const regex = /data_type=([^,]*)/;
      const match = regex.exec(errorResponse);
      const existDataType = match ? match[1] : null;

      if (existDataType === 'AssetTypeName') {
        alertDialogOptions.message = `同名の設定テンプレートがすでに存在します。`;
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        return;
      }

      if (existDataType === 'NoneAssetTypeId') {
        alertDialogOptions.message = `対象の設定テンプレートが存在しません。`;
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
        return;
      }
    }

    this.dialog.open(AlertDialogComponent, {
      width: '600px',
      data: alertDialogOptions,
    });
  }

  /**
   * 設定テンプレートコピー時、文字列もしくは数値を判定し付加する
   * @param assetTypeRequest 設定テンプレートリクエスト
   * @returns
   */
  private appendAssetTypeName(
    assetTypeRequest: AssetTypeRequest,
  ): AssetTypeRequest {
    const existingNames: string[] = this.assetTypeList.map(
      (asset) => asset.assettypeName,
    );

    const originalName = assetTypeRequest.assettypeName;
    const suffixIndex = originalName.lastIndexOf(COPY_SUFFIX);
    const hadCopySuffix = suffixIndex !== -1;
    const baseName = hadCopySuffix
      ? originalName.substring(0, suffixIndex)
      : originalName;

    const escapeRegExp = (text: string) =>
      text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const copyNamePattern = new RegExp(
      `^${escapeRegExp(baseName)}${escapeRegExp(COPY_SUFFIX)}(\\d+)?$`,
    );

    let maxFoundNumber = 0;

    for (const existingName of existingNames) {
      // (xxがbaseName、yyがnumberとして）"xx_コピーyy" or "xx" の形式の場合のみ処理を続ける
      const matchResult = existingName.match(copyNamePattern);
      if (!matchResult) continue;

      // すでに存在するコピーに続くインデックスの最大値（maxFoundNumber）を取得する。
      const numericPart = matchResult[1];
      if (numericPart) {
        const parsedNumber = parseInt(numericPart, 10);
        if (!isNaN(parsedNumber) && parsedNumber > maxFoundNumber) {
          maxFoundNumber = parsedNumber;
        }
      } else {
        // "_コピー" のみが存在する場合は 1 と見なす
        if (1 > maxFoundNumber) maxFoundNumber = 1;
      }
    }

    if (maxFoundNumber === 0) {
      // 既存にコピーが無い場合
      assetTypeRequest.assettypeName = baseName + COPY_SUFFIX;
    } else {
      // 既にコピーがあれば最大 + 1 を付与
      assetTypeRequest.assettypeName =
        baseName + COPY_SUFFIX + String(maxFoundNumber + 1);
    }

    return assetTypeRequest;
  }

  /**
   * 設定テンプレート一覧を取得する
   */
  private _initAssetTypeList() {
    this.assetTypeService.findAll().subscribe({
      next: (assetTypeList) => {
        this.assetTypeList = assetTypeList;

        // 設定テンプレートのリストを取得
        const displayNames = assetTypeList.map(
          (assetType) => assetType.deviceTypeDisplayName || '',
        );
        this.deviceTypes = Array.from(new Set(displayNames))
          .filter(Boolean)
          .sort(compareDisplayNames);
      },
      error: () => {},
    });
  }
}
